import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { GroupId, ProjectId } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  fetchCompanyMemberGroupDetails,
  fetchCompanyMemberGroups,
} from "@store/company-member-groups/company-member-groups-thunk";
import {
  BaseEntityAdapter,
  BaseEntityState,
  FetchingStatus,
} from "@store/store-types";

/** Entity stored per group in the adapter */
export interface CompanyMemberGroupEntity
  extends FaroCoreApiTypes.GroupSummaryResponse {
  /** IDs of the projects belonging to this group. Populated on-demand when group details are fetched. */
  projectIds?: ProjectId[];
}

/** Creates an entity adapter to store a map with all the group details that belong to a company member */
export const companyMemberGroupDetailsAdapter: BaseEntityAdapter<CompanyMemberGroupEntity> =
  createEntityAdapter({
    selectId: (group) => group.id,
  });

/**
 * State company member groups.
 */
export interface CompanyMemberGroupsState
  extends BaseEntityState<CompanyMemberGroupEntity> {
  /** State of the groups list endpoint */
  status: FetchingStatus;

  /** Per-group fetching status for group details */
  groupDetailsStatus: Record<GroupId, FetchingStatus>;

  /** Map of all fetched member-group projects for direct lookup by ID */
  projectsMap: Record<ProjectId, FaroCoreApiTypes.ProjectSummary>;
}

const initialState: CompanyMemberGroupsState = {
  ...companyMemberGroupDetailsAdapter.getInitialState(),
  status: "uninitialized",
  groupDetailsStatus: {},
  projectsMap: {},
};

/**
 * Slice to access state of loaded company member groups
 */
const companyMemberGroupsSlice = createSlice({
  name: "companyMemberGroups",
  initialState,
  reducers: {
    /**
     * Resets the state of the company member groups slice.
     */
    resetCompanyMemberGroupsState: () => initialState,

    /** Removes a single group from the adapter by ID */
    removeGroup(state, action: PayloadAction<string>) {
      const groupId = action.payload;
      const group = state.entities[groupId];
      if (group?.projectIds) {
        for (const projectId of group.projectIds) {
          delete state.projectsMap[projectId];
        }
      }
      delete state.groupDetailsStatus[groupId];
      companyMemberGroupDetailsAdapter.removeOne(state, groupId);
    },

    /** Removes projects from a group by their IDs */
    removeProjectsFromGroup(
      state,
      action: PayloadAction<{ groupId: GroupId; projectIds: ProjectId[] }>
    ) {
      const { groupId, projectIds: idsToRemove } = action.payload;
      const group = state.entities[groupId];
      if (group?.projectIds) {
        companyMemberGroupDetailsAdapter.updateOne(state, {
          id: groupId,
          changes: {
            projectIds: group.projectIds.filter(
              (id) => !idsToRemove.includes(id)
            ),
          },
        });
      }
      for (const projectId of idsToRemove) {
        delete state.projectsMap[projectId];
      }
    },

    /** Updates the role of a project in the projects map */
    updateProjectRoleInMap(
      state,
      action: PayloadAction<{
        projectId: ProjectId;
        newRole: FaroCoreApiTypes.ProjectRole;
      }>
    ) {
      const { projectId, newRole } = action.payload;
      const project = state.projectsMap[projectId];
      if (project) {
        project.role = newRole;
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(
        fetchCompanyMemberGroups.pending,
        (state: CompanyMemberGroupsState) => {
          state.status = "pending";
        }
      )
      .addCase(
        fetchCompanyMemberGroups.fulfilled,
        (state: CompanyMemberGroupsState, action) => {
          companyMemberGroupDetailsAdapter.setAll(state, action.payload);
          state.status = "succeeded";
        }
      )
      .addCase(
        fetchCompanyMemberGroups.rejected,
        (state: CompanyMemberGroupsState) => {
          state.status = "rejected";
        }
      )
      .addCase(fetchCompanyMemberGroupDetails.pending, (state, action) => {
        state.groupDetailsStatus[action.meta.arg.groupId] = "pending";
      })
      .addCase(fetchCompanyMemberGroupDetails.fulfilled, (state, action) => {
        const { groupId } = action.meta.arg;
        const { projects, projectIds } = action.payload;
        companyMemberGroupDetailsAdapter.updateOne(state, {
          id: groupId,
          changes: { projectIds },
        });

        for (const project of projects) {
          state.projectsMap[project.id] = project;
        }

        state.groupDetailsStatus[groupId] = "succeeded";
      })
      .addCase(fetchCompanyMemberGroupDetails.rejected, (state, action) => {
        state.groupDetailsStatus[action.meta.arg.groupId] = "rejected";
      });
  },
});

export const {
  resetCompanyMemberGroupsState,
  removeGroup,
  removeProjectsFromGroup,
  updateProjectRoleInMap,
} = companyMemberGroupsSlice.actions;

export const companyMemberGroupsReducer = companyMemberGroupsSlice.reducer;
